import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Chip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Restore as RestoreIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../hooks/useTranslation';
import CycleService from '../../services/cycle.service';

const DeletedCyclesList = () => {
  const { hasPermission } = useAuth();
  const { t } = useTranslation();
  const [cycles, setCycles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [restoreDialog, setRestoreDialog] = useState({ open: false, id: null, name: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const navigate = useNavigate();

  useEffect(() => {
    fetchDeletedCycles();
  }, []);

  const fetchDeletedCycles = async () => {
    setLoading(true);
    try {
      const response = await CycleService.getTrashed();
      setCycles(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching deleted cycles:', error);
      showSnackbar(t('cycles.errorLoadingDeletedCycles') + ': ' + (error.response?.data?.message || error.message), 'error');
      setCycles([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = (id, name) => {
    setRestoreDialog({ open: true, id, name });
  };

  const confirmRestore = async () => {
    try {
      await CycleService.restore(restoreDialog.id);
      showSnackbar(t('cycles.cycleRestoredSuccess'));
      fetchDeletedCycles();
    } catch (error) {
      showSnackbar(t('cycles.errorRestoringCycle'), 'error');
    } finally {
      setRestoreDialog({ open: false, id: null, name: null });
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const filteredCycles = cycles.filter(cycle =>
    cycle.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <Box>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box display="flex" alignItems="center" gap={2}>
              <IconButton onClick={() => navigate('/cycles')}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h5">{t('cycles.deletedCycles')}</Typography>
            </Box>
          </Box>

          <TextField
            placeholder={t('cycles.searchDeletedPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ mb: 2, minWidth: 300 }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
          />

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('cycles.name')}</TableCell>
                  <TableCell>{t('cycles.deletedAt')}</TableCell>
                  <TableCell align="center">{t('common.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : filteredCycles.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      {t('cycles.noDeletedCyclesFound')}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredCycles.map((cycle) => (
                    <TableRow key={cycle.id} hover>
                      <TableCell>{cycle.name}</TableCell>
                      <TableCell>{formatDate(cycle.deleted_at)}</TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          {hasPermission('cycles.edit') && (
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleRestore(cycle.id, cycle.name)}
                              title={t('cycles.restoreCycle')}
                            >
                              <RestoreIcon />
                            </IconButton>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog
        open={restoreDialog.open}
        onClose={() => setRestoreDialog({ open: false, id: null, name: null })}
      >
        <DialogTitle>{t('cycles.restoreCycleTitle')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('cycles.restoreCycleConfirm', { name: restoreDialog.name })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestoreDialog({ open: false, id: null, name: null })}>
            {t('common.cancel')}
          </Button>
          <Button onClick={confirmRestore} color="primary" variant="contained">
            {t('cycles.restoreCycle')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DeletedCyclesList;
